import { useEffect, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { LuArrowRightLeft, LuBookOpenText, LuBot } from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { TrelloCardSummary } from '../../entities/trello-card';
import { getTrelloBoardCards, getTrelloBoardStats } from '../../shared/api/dashboard';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { useAsyncStat } from '../../shared/hooks/use-async-stat';
import { contrastTextColor } from '../../shared/utils/color';
import { asISODate, fmtDaysFromHours, fmtPct, safeNum } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, StatEmpty, StatError, StatLoading } from '../../widgets/stats';
import TransferModal from '../../widgets/transfer/TransferModal';
import { ActionsMenu } from '../../widgets/ui/ActionsMenu';

export function TrelloBoardStatsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dateRangeCtx = useDateRange();
  const project = useProjectById(projectId);
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const [cards, setCards] = useState<TrelloCardSummary[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setCardsLoading(true);
    setCardsError(null);
    getTrelloBoardCards(projectId)
      .then(({ cards }) => {
        if (!cancelled) setCards(cards);
      })
      .catch(() => {
        if (!cancelled) setCardsError('Failed to load cards.');
      })
      .finally(() => {
        if (!cancelled) setCardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const {
    data: stat,
    loading,
    error,
  } = useAsyncStat(
    async () => {
      if (!projectId || !dateRangeCtx?.from || !dateRangeCtx?.to) return null;
      const range = { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) };
      const { stat } = await getTrelloBoardStats(projectId, range);
      return stat;
    },
    [projectId, dateRangeCtx?.from, dateRangeCtx?.to],
    'Failed to load Trello board stats.',
  );

  useEffect(() => {
    setDashHeader({
      component: 'project',
      breadcrumb: (
        <Link to="/dashboard/projects" className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message="Projects" position="bottom">
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: project?.name ?? 'Trello board',
      subtitle: <span className="badge badge-sm badge-outline">Trello</span>,
      actions: (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-primary btn-sm shadow-glow gap-1.5"
            onClick={() =>
              openChat({
                projectId,
                connectedAgents: ['TrelloAgent'],
                suggestedPrompts: [
                  'What are the main delivery risks on this board?',
                  'Which cards should I prioritize?',
                  ...(safeNum(stat?.reopenRatePercent) > 15 ? ['Why is the reopen rate high?'] : []),
                ],
                analyticsContext: {
                  scope: 'project',
                  projectIds: projectId ? [projectId] : undefined,
                  dateRange:
                    dateRangeCtx?.from && dateRangeCtx?.to
                      ? { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) }
                      : undefined,
                },
              })
            }
          >
            <LuBot size={14} />
            Ask mFlow
          </button>
          <ActionsMenu
            items={[
              { label: 'Knowledge', icon: LuBookOpenText, to: `/dashboard/projects/${projectId}/knowledge` },
              { label: 'Transfer…', icon: LuArrowRightLeft, onClick: () => setIsTransferModalOpen(true) },
            ]}
          />
        </div>
      ),
    });
    return () => setDashHeader({});
  }, [project?.name, projectId, stat, dateRangeCtx?.from, dateRangeCtx?.to]);

  if (loading) return <StatLoading />;
  if (error) return <StatError message={error} />;
  if (!stat) return <StatEmpty message="No data available." />;

  const reopenHigh = safeNum(stat.reopenRatePercent) > 15;
  const activeCards = cards.filter((c) => !c.closed);

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <KpiCard
            label={
              <>
                Created <InfoTip text="Cards created in the selected range." />
              </>
            }
            value={stat.createdCount}
            footer={`Bugs: ${stat.bugCount}`}
          />

          <KpiCard
            label={
              <>
                Completed <InfoTip text="Cards moved into the board's done list in this range." />
              </>
            }
            value={stat.completedCount}
            footer={
              stat.deliveryAccuracyPercent != null
                ? `On-time: ${fmtPct(stat.deliveryAccuracyPercent)}`
                : `On-time completed: ${stat.onTimeCompletedCount}`
            }
          />

          <KpiCard
            label={
              <>
                Cycle time <InfoTip text="Average time from card creation to landing in the done list." />
              </>
            }
            value={stat.cycleTimeAvgHours != null ? fmtDaysFromHours(stat.cycleTimeAvgHours) : '—'}
            footer={`WIP now: ${stat.wipCount}`}
          />

          <KpiCard
            label={
              <>
                Reopen rate <InfoTip text="Cards moved backward out of the done list, as a share of completed cards." />
              </>
            }
            value={<span className={reopenHigh ? 'text-error' : ''}>{fmtPct(safeNum(stat.reopenRatePercent))}</span>}
            footer={`Reopen events: ${stat.reopenEvents}`}
          />
        </div>

        <div className="alert bg-base-100 shadow-card">
          <span className="text-sm opacity-70">
            Trello has no fixed status taxonomy — &quot;done&quot; is inferred positionally (last active list on the
            board). Flow efficiency and sprint-relative delivery aren&apos;t computable for Trello boards.
          </span>
        </div>

        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="text-lg font-semibold">Cards</div>
            <div className="text-sm opacity-70">Open a card for its description, labels, and comments</div>

            {cardsLoading ? (
              <div className="py-6 flex justify-center">
                <div className="loading loading-spinner" />
              </div>
            ) : cardsError ? (
              <div className="py-6 text-error text-sm">{cardsError}</div>
            ) : !activeCards.length ? (
              <div className="py-6 opacity-70">No active cards on this board.</div>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Card</th>
                      <th>Labels</th>
                      <th>Due</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCards.map((c) => (
                      <tr key={c.cardId}>
                        <td className="max-w-[420px] truncate">{c.name}</td>
                        <td className="space-x-1">
                          {c.labels.map((l) => (
                            <span
                              key={l.id}
                              className="badge badge-sm border-0"
                              style={{ backgroundColor: l.color, color: contrastTextColor(l.color) }}
                            >
                              {l.name}
                            </span>
                          ))}
                        </td>
                        <td>{c.due ? new Date(c.due).toLocaleDateString() : '—'}</td>
                        <td>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => navigate(`/dashboard/projects/${projectId}/cards/${c.cardId}`)}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {isTransferModalOpen && projectId && (
        <TransferModal
          sourceProvider="trello"
          sourceProjectId={projectId}
          sourceProjectName={project?.name}
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}
    </div>
  );
}
